/**
 * BCH address lookups — now powered directly by public Fulcrum (ElectrumX)
 * servers over WSS, straight from the browser (see src/lib/fulcrum.ts).
 *
 * We USED to proxy a stack of flaky web explorers through a Netlify Function.
 * They all rate-limited / bot-blocked / went down at once ("backend: none"),
 * so that approach was replaced with the indexer BCH wallets actually use:
 * Fulcrum. No server, no proxy, no CORS — the browser connects to the Fulcrum
 * WSS pool and asks it directly, with automatic failover between servers.
 *
 * This module keeps the SAME public shape the rest of the app relied on:
 *   fetchUtxos(address)  → { utxos, txids, txCount, backend, errors }
 *   fetchTxids(address)  → { ... }
 *   fetchTxCount(address) → number
 */

import { fulcrumGetHistory, fulcrumListUnspent } from '@/lib/fulcrum';

export interface ExplorerUtxo {
  txid: string;
  value: number; // satoshis
}

export interface ExplorerResult {
  utxos: ExplorerUtxo[];
  txids: string[];
  txCount: number | null;
  backend: string | null; // which Fulcrum server answered
  errors: string[];
}

/** Fetch unspent outputs for an address. */
export async function fetchUtxos(address: string): Promise<ExplorerResult> {
  try {
    const { utxos, server } = await fulcrumListUnspent(address);
    return {
      utxos: utxos.map((u) => ({ txid: u.tx_hash, value: Number(u.value) })),
      txids: [],
      txCount: null,
      backend: server,
      errors: [],
    };
  } catch (e) {
    return {
      utxos: [],
      txids: [],
      txCount: null,
      backend: null,
      errors: [e instanceof Error ? e.message : String(e)],
    };
  }
}

/** Fetch recent transaction ids for an address. */
export async function fetchTxids(address: string): Promise<ExplorerResult> {
  try {
    const { history, server } = await fulcrumGetHistory(address);
    const txids = history.map((h) => h.tx_hash);
    return {
      utxos: [],
      txids,
      txCount: txids.length,
      backend: server,
      errors: [],
    };
  } catch (e) {
    return {
      utxos: [],
      txids: [],
      txCount: null,
      backend: null,
      errors: [e instanceof Error ? e.message : String(e)],
    };
  }
}

/** Fetch the total transaction count for an address (used for Pro purchase count). */
export async function fetchTxCount(address: string): Promise<number> {
  const { history } = await fulcrumGetHistory(address);
  return history.length;
}
